<template>
    <div class="chart-builder mx-2">
        <div class="card my-3">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col">
                        <label class="form-label">Chart Name</label>
                        <input type="text" id="chart-name-input" class="form-control" v-model="local_chart.name"
                                :class="{'is-invalid': errors.name !== ''}"  @change="clearErrorForField('name')" aria-label="Chart Name">
                        <div class="text-danger invalid-feedback" v-if="errors.name !== ''">
                            {{ errors.name }}
                        </div>
                    </div>
                    <div class="col-sm-3" v-if="!local_chart.id">
                        <label class="form-label">Client <em>(Optional)</em></label>
                        <select class="form-select" id="client-select" v-model="local_client"
                            @change="updateDataBlend()"  aria-label="Local Client">
                            <option v-if="!clients?.length" value="" disabled>No clients available</option>
                            <option v-for="i in clients || []" :key="i.id" :value="i">{{ i.name }}</option>
                        </select>
                    </div>
                    <div class="col-sm-3" v-if="!local_chart.id">
                        <label class="form-label">Data Blend Type</label>
                        <select class="form-select" v-model="local_chart.data_blend_type" @change="updateDataBlend()" aria-label="Data Blend Type">
                            <option v-if="!local_client.data_blends?.length" value="" disabled>No data blends available</option>
                            <option v-for="i in local_client.data_blends || []" :key="i.type" :value="i.type">{{ propercase(i.type) }}</option>
                        </select>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col">
                        <label class="form-label">Chart Title</label>
                        <input type="text" id="title-input" class="form-control" v-model="local_chart.title" aria-label="Chart Title">
                    </div>
                    <div class="col">
                        <label class="form-label">{{ (local_chart.type === 'scorecard') ? 'Image URL' : 'Chart Subtitle' }} <em>(Often phrased as a question)</em></label>
                        <input type="text" id="subtitle-input" class="form-control" v-model="local_chart.subtitle" aria-label="Subtitle">
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col">
                        <label class="form-label">Chart Description <em>(Used in search and as a <i class="fas fa-question-circle info-icon me-0"></i> icon next to chart title.)</em></label>
                        <textarea class="form-control" v-model="local_chart.description"></textarea>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div :class="render_large_chart ? 'col-12' : 'col-3'">
                <chart-configuration 
                    :chart="local_chart" 
                    :render_large_chart="render_large_chart" 
                    :data_blends_array="clients[0]?.data_blends || []"
                    :errors="errors" 
                    :metrics_filters="metrics_filters" 
                    :clients="clients"
                    :validate_data_blend="validate_data_blend"
                    ref="chart_config"
                    @updateChartConfig="updateChartConfig" 
                    @updateChartData="updateChartData" 
                    @loadingChartData="loadingChartData">
                </chart-configuration>
            </div>
            <div :class="render_large_chart ? 'col-12 mt-3' : 'col-9'">
                <chart-preview 
                    :chart_selected="local_chart" 
                    :chart_type="local_chart.type" 
                    :if_loading="is_loading_chart_data" 
                    :render_large_chart="render_large_chart"  
                    :data_blends="data_blends"
                    @changePreviewSize="changePreviewSize"
                    @validateDataBlendChange="validateDataBlendChange">
                </chart-preview>
            </div>
        </div>

        <div class="row">
            <div class="col">
                <div class="alert alert-danger mt-3" v-if="form.error">
                    There was an error saving the chart details.
                </div>
                <!-- Update Button -->
                <div class="mt-3 mb-5">
                    <button type="submit" class="btn btn-primary"
                            @click.prevent="update" :disabled="form.busy">
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> {{ local_chart.id !== null ? "Update" : "Add" }} 
                    </button>
                    <a href="/admin/charts" class="btn btn-white ms-3">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'ChartBuilder',
    components: {
        Swal
    },
    props: {
        chart: {
            type: Object,
            required: true
        },
        metrics_filters: {
            type: Array,
            required: true
        },
        clients: {
            type: Array,
            required: true
        },
        data_blends: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            local_chart: {
                chart_type: '',
            },
            local_client: {
                id: null,
                name: "",
                data_blends: [],
                url: null
            },
            render_large_chart: false,
            form: {
                busy: false,
                error: false
            },
            is_loading_chart_data: false,
            validate_data_blend: null,
            errors: {
                name: '',
                title: '',
                subtitle: '',
                chart_type: '',
                chart_size: '',
                group_by: '',
                metrics: '',
                filters: '',
                order_by: ''
            }
        };
    },
    beforeMount() {
        this.local_chart = this.chart || {};

        // Check if clients array exists and has items
        if (!this.clients || !Array.isArray(this.clients) || this.clients.length === 0) {
            console.error("No clients available");
            return;
        }

        //If it is a new chart
        if(!this.local_chart.id){
            //Pick the first client (which is Global)
            this.local_client = this.clients[0] || {
                id: null,
                name: "Global",
                data_blends: [],
                url: null
            };

            // Check if the client has data_blends
            if (!this.local_client.data_blends || !Array.isArray(this.local_client.data_blends) || this.local_client.data_blends.length === 0) {
                console.error("Selected client has no data blends");
                return;
            }

            //Also pick the datablend based on type
            if (this.local_chart.data_blend_type){
                var index = this.local_client.data_blends.findIndex((db) => db.type == this.local_chart.data_blend_type);
                if (index !== -1) {
                    this.local_chart.data_blend_id = this.local_client.data_blends[index].id;
                    this.local_chart.data_blend = this.local_client.data_blends[index];
                } else {
                    // If the specified data_blend_type doesn't exist, use the first one
                    this.local_chart.data_blend_type = this.local_client.data_blends[0].type;
                    this.local_chart.data_blend = this.local_client.data_blends[0];
                }
            } else {
                //pick the first data blend when creating a new chart
                this.local_chart.data_blend_type = this.local_client.data_blends[0].type;
                this.local_chart.data_blend = this.local_client.data_blends[0];
            }
        }
        //If it is a template chart
        else if(this.local_chart.client_id == null){
            let foundMatch = false;

            for(let i = 0; i < this.clients.length; i++){
                // Check if client has data_blends
                if (!this.clients[i].data_blends || !Array.isArray(this.clients[i].data_blends)) {
                    continue;
                }

                for(let j = 0; j < this.clients[i].data_blends.length; j++){
                    //Find the first client that has the right data blend
                    if(this.local_chart.data_blend_type == this.clients[i].data_blends[j].type){
                        this.local_client = this.clients[i];
                        this.local_chart.data_blend = this.local_client.data_blends[j];
                        foundMatch = true;
                        break;
                    }
                }

                if (foundMatch) {
                    break;
                }
            }

            // If no matching data blend was found, use the first client and its first data blend
            if (!foundMatch && this.clients[0] && this.clients[0].data_blends && this.clients[0].data_blends.length > 0) {
                this.local_client = this.clients[0];
                this.local_chart.data_blend = this.local_client.data_blends[0];
                this.local_chart.data_blend_type = this.local_chart.data_blend.type;
            }
        }
        else {
            // Use the chart's client if it exists
            if (this.local_chart.client) {
                this.local_client = this.local_chart.client;
            } else {
                // Try to find the client by ID
                const client = this.clients.find(c => c.id === this.local_chart.client_id);
                if (client) {
                    this.local_client = client;
                } else {
                    // Fallback to the first client
                    this.local_client = this.clients[0] || {
                        id: null,
                        name: "Global",
                        data_blends: [],
                        url: null
                    };
                }
            }
        }
    },
    methods: {
        changePreviewSize(size) {
            if (this.local_chart.type == 'information_card')
                this.render_large_chart = true;
            else
                this.render_large_chart = (size == 'xl');
        },
        updateChartConfig(chartConfig){
            this.local_chart.data = null;
            this.local_chart.type = chartConfig.type;
            if (chartConfig.type == 'information_card'){
                this.render_large_chart = true;
            }
            else{
                this.render_large_chart = false;
            }
            this.$forceUpdate();
        },
        updateChartData(chartData){
            this.local_chart.data = chartData.data;
            this.local_chart.labels = chartData.labels;
            this.local_chart.metrics = chartData.metrics;
            this.local_chart.group_by = chartData.group_by;
            this.local_chart.metric_formats = chartData.metric_formats;
            this.local_chart.html = chartData.html;
            this.local_chart.icon = chartData.icon;
            this.local_chart.icon_background_color = chartData.icon_background_color;
            this.local_chart.if_card_white = chartData.if_card_white;
            this.is_loading_chart_data = false;
            this.$forceUpdate();
        },
        loadingChartData(is_loading){
            this.is_loading_chart_data = is_loading;
        },
        updateDataBlend(){
            // Check if local_client exists
            if (!this.local_client) {
                console.error("Local client is not defined");
                return;
            }

            this.local_chart.client = this.local_client;

            // If the client has an ID and it's not -1 (global), fetch datablends from the backend
            if (this.local_client.id && this.local_client.id !== -1) {
                // Check if the client has a URL property
                if (!this.local_client.url) {
                    console.error("Client URL is missing");
                    return;
                }

                // Show loading state if needed
                this.form.busy = true;

                // Make an API call to get the datablends for the selected client
                window.axios.get(`/api/client/${this.local_client.url}/datablends`)
                    .then(response => {
                        // Update local_client.data_blends with the response from the API
                        this.local_client.data_blends = response.data.data || [];

                        // Continue with the data blend selection logic
                        this.processDataBlendSelection();

                        // Hide loading state
                        this.form.busy = false;
                    })
                    .catch(error => {
                        console.error("Error fetching datablends:", error);
                        this.local_client.data_blends = [];
                        this.form.busy = false;
                        this.form.error = true;
                        setTimeout(() => { this.form.error = false; }, 10000);
                    });
            } else {
                // For global client, use the existing data_blends
                this.processDataBlendSelection();
            }
        },
        processDataBlendSelection() {
            // Check if local_client has data_blends
            if (!this.local_client.data_blends || !Array.isArray(this.local_client.data_blends) || this.local_client.data_blends.length === 0) {
                console.error("Selected client has no data blends");
                return;
            }

            //If the new client doesn't have the same type of data blends available
            if(this.local_chart.data_blend_type == null){
                this.local_chart.data_blend_type = this.local_client.data_blends[0].type;
                //Copy this data blend
                this.local_chart.data_blend = this.local_client.data_blends[0];
                if(this.local_client.id != null){
                    this.local_chart.data_blend_id = this.local_chart.data_blend.id;
                    this.local_chart.client_id = this.local_client.id;
                }
                else this.local_chart.data_blend_id = null;
            }
            //Both are still good so just need to find the right data blend with the same type
            else {
                let foundMatchingBlend = false;

                for(let i = 0; i < this.local_client.data_blends.length; i++){
                    if(this.local_client.data_blends[i].type == this.local_chart.data_blend_type){
                        this.local_chart.data_blend = this.local_client.data_blends[i];
                        //If it is the global one
                        if(this.local_client.id != null){
                            this.local_chart.data_blend_id = this.local_chart.data_blend.id;
                            this.local_chart.client_id = this.local_client.id;
                        }
                        else this.local_chart.data_blend_id = null;

                        foundMatchingBlend = true;
                        break;
                    }
                }

                // If no matching data blend was found, use the first one
                if (!foundMatchingBlend) {
                    console.warn(`No data blend of type '${this.local_chart.data_blend_type}' found for the selected client. Using the first available data blend.`);
                    this.local_chart.data_blend_type = this.local_client.data_blends[0].type;
                    this.local_chart.data_blend = this.local_client.data_blends[0];
                    if(this.local_client.id != null){
                        this.local_chart.data_blend_id = this.local_chart.data_blend.id;
                        this.local_chart.client_id = this.local_client.id;
                    }
                    else this.local_chart.data_blend_id = null;
                }
            }

            // Create a new object to trigger reactivity
            this.local_chart = Object.assign({}, this.local_chart);

            // Refresh columns if chart_config ref exists
            if (this.$refs.chart_config && typeof this.$refs.chart_config.refreshColumns === 'function') {
                this.$refs.chart_config.refreshColumns();
            }
        },
        clearErrorForField(field_name){
            this.errors[field_name] = '';
        },
        clearErrors() {//reset errors to empty strings
            this.errors = {
                name:''
            }
        },
        hasErrors() {//validate the form
            if(this.local_chart.name == null || this.local_chart.name == ""){
                this.errors.name = "A chart name is required.";
            } else
                this.errors.name = "";

            // Validate the form in chart-configuration component
            let config_not_valid = this.$refs.chart_config.hasErrors();

            if (this.errors.name!="" || config_not_valid){
                return true
            } else{
                return false
            }
        },
        validateDataBlendChange(data_blend){
            this.validate_data_blend = data_blend;
        },
        update() {
            this.clearErrors();

            //To trigger the refresh of the data source, I have to clone the chart but that disconnects
            // it from the one in the config. I'm sure there is a better way to do this but here we are.
            let chart = this.$refs.chart_config.getChart();
            this.local_chart.html = chart.html;
            this.local_chart.include_projections = chart.include_projections;
            this.local_chart.highlight_last_value = chart.highlight_last_value;
            this.local_chart.show_all_data_labels = chart.show_all_data_labels;
            this.local_chart.show_series_percentage = chart.show_series_percentage;
            this.local_chart.max_results = chart.max_results;
            this.local_chart.filter = chart.filter;
            this.local_chart.metrics = chart.metrics;
            this.local_chart.type = chart.type;
            this.local_chart.group_by = chart.group_by;
            this.local_chart.icon = chart.icon;
            this.local_chart.class_name = chart.class_name;
            this.local_chart.icon_background_color = chart.icon_background_color;

            this.updateText(this.local_chart.group_by);
            this.updateText(this.local_chart.metrics);

            if (this.local_chart.type != 'information_card'){
                if(this.hasErrors()) {
                    this.form.error = true;
                    return;
                }
                this.local_chart.order_by = this.$refs.chart_config.getOrderBys();
            } else { // save the information_card content to html field instead of data
                if(this.local_chart.name == null || this.local_chart.name == ""){
                    this.errors.name = "A chart name is required.";
                    this.form.error = true;
                    return;
                } else
                    this.errors.name = "";
            }

            this.form.busy = true;

            //Make a new chart
            if(this.local_chart.id == null) {
                window.axios.post('/api/chart', this.local_chart)
                  .then(response => {
                    // //redirect back to the chart builder
                    document.location = "/admin/charts";
                  }).catch(error => {
                    this.form.error = true;
                    setTimeout( function() {self.form.error = false} , 10000);
                  });
            } else {
                window.axios.put('/api/chart/' + this.local_chart.id, this.local_chart)
                  .then(response => {
                    document.location = "/admin/charts";
                  }).catch(error => {
                    this.form.error = true;
                    setTimeout( function() {self.form.error = false} , 10000);
                  });
            }

            //Now change them back
            if(this.local_chart.metrics && this.local_chart.metrics.length > 0)
                for(var i = 0; i < this.local_chart.metrics.length; i++)
                    if(this.local_chart.metrics[i].admin_name)
                        this.local_chart.metrics[i].text = this.local_chart.metrics[i].admin_name;

            if(this.local_chart.group_by && this.local_chart.group_by.length > 0)
                for(var i = 0; i < this.local_chart.group_by.length; i++){
                    if(this.local_chart.group_by[i].admin_name)
                        this.local_chart.group_by[i].text = this.local_chart.group_by[i].admin_name;
                }
        },
        updateText(arr){
            if(arr && arr.length > 0)
                for(var i = 0; i < arr.length; i++){
                    if (arr[i].display_name)
                        arr[i].text = arr[i].display_name;
                    //If value exists and is a string
                    else if(arr[i].value && typeof arr[i].value === 'string') {
                        //Split the value by . and then, take the last value and call the propercase filter
                        let split = arr[i].value.split('.');
                        //Replace _ with a space and capitalize the first letter of each word
                        let value = String(split[split.length-1]).replaceAll("_", " ");

                        value=  value.replace(
                            /\w\S*/g,
                            function(txt) {
                                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                            });
                        arr[i].text = value;
                    }
                }
        }
    }
});
</script>
